const GRID_SIZE = 56

export type TronGridVariant = "homepage" | "footer"

export interface TronPlusParticle {
  cx: number
  cy: number
  half: number
  opacity: number
  stroke: string
  twinkleDuration: number
  twinkleDelay: number
  glow: boolean
}

export interface TronGridNode {
  cx: number
  cy: number
  r: number
  opacity: number
}

const createSeededParticles = (
  count: number,
  seedMultiplier: number,
  yMin: number,
  yRange: number,
): TronPlusParticle[] => {
  const particles: TronPlusParticle[] = []

  for (let i = 0; i < count; i++) {
    const s = (i * seedMultiplier) >>> 0
    particles.push({
      cx: (s % 1900) + 10,
      cy: yMin + ((s >> 10) % yRange),
      half: 2 + (s % 3),
      opacity: 0.05 + (s % 12) / 100,
      stroke: s % 5 === 0 ? "#818cf8" : s % 7 === 0 ? "#93c5fd" : "white",
      twinkleDuration: 4 + (s % 4),
      twinkleDelay: (s % 16) / 8,
      glow: s % 9 === 0,
    })
  }

  return particles
}

const createGridAlignedParticles = (
  rows: number,
  cols: number,
  yStart: number,
  densityModulo: number,
): TronPlusParticle[] => {
  const particles: TronPlusParticle[] = []

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const s = ((row * cols + col) * 2654435761) >>> 0
      if (s % densityModulo !== 0) continue

      particles.push({
        cx: col * GRID_SIZE + GRID_SIZE / 2,
        cy: yStart + row * GRID_SIZE,
        half: 2 + (s % 3),
        opacity: 0.08 + (s % 10) / 100,
        stroke: s % 7 === 0 ? "#93c5fd" : s % 3 === 0 ? "#6366f1" : "white",
        twinkleDuration: 3 + (s % 5),
        twinkleDelay: (s % 20) / 10,
        glow: s % 6 === 0,
      })
    }
  }

  return particles
}

const createGridNodes = (rows: number, cols: number, yStart: number, densityModulo: number): TronGridNode[] => {
  const nodes: TronGridNode[] = []

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const s = ((row * cols + col) * 2246822519) >>> 0
      if (s % densityModulo !== 0) continue

      nodes.push({
        cx: col * GRID_SIZE + GRID_SIZE / 2,
        cy: yStart + row * GRID_SIZE,
        r: 1 + (s % 2),
        opacity: 0.12 + (s % 8) / 100,
      })
    }
  }

  return nodes
}

export const HOMEPAGE_TRON_PLUS_PARTICLES: readonly TronPlusParticle[] = [
  ...createGridAlignedParticles(14, 34, 80, 5),
  ...createSeededParticles(64, 1597334677, 40, 980),
]

export const HOMEPAGE_TRON_GRID_NODES: readonly TronGridNode[] = createGridNodes(12, 28, 100, 6)

export const FOOTER_TRON_PLUS_PARTICLES: readonly TronPlusParticle[] = [
  ...createGridAlignedParticles(6, 34, 620, 4),
  ...createSeededParticles(48, 1597334677, 540, 340),
]

export const FOOTER_TRON_GRID_NODES: readonly TronGridNode[] = createGridNodes(5, 28, 640, 5)

export const TRON_GRID_VIEWBOX = {
  homepage: { width: 1920, height: 1080 },
  footer: { width: 1920, height: 896 },
} as const
